import PageShell from "@/components/PageShell";
import usePageMeta from "@/lib/usePageMeta";
import { motion } from "framer-motion";
import { Mail, MessageCircle, Send } from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export const Contact = () => {
  usePageMeta({
    title: "Contact Postmark & Co. — Direct Mail & Postcard Marketing Questions",
    description:
      "Get in touch with Postmark & Co. about postcard marketing, EDDM campaigns, targeted mailing lists, or media inquiries. Independent editorial team based in the United States.",
    canonical: "https://maildirect.store/contact",
  });

  return (
    <PageShell testid="contact-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <motion.div variants={fade} initial="hidden" animate="show">
          <div className="postmark text-[#D93829] mb-6">
            <span className="w-1 h-1 rounded-full bg-[#D93829]" />
            Return Address
          </div>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-[1.05] tracking-tight text-[#1C1C1A]">
            Write us <span className="italic text-[#D93829]">back</span>.
          </h1>
          <p className="mt-6 text-[#3B3A36] text-lg leading-relaxed font-sans-body max-w-xl">
            Editorial questions, correction requests, media inquiries, or
            partnership pitches — all go to the same inbox. We reply within
            two business days.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          <a
            href="mailto:hello@maildirect.store"
            className="group polaroid block rotate-[-2deg] hover:rotate-0 transition-transform duration-500"
            data-testid="contact-email-card"
          >
            <div className="bg-[#EFECE5] h-40 flex items-center justify-center">
              <Mail size={40} className="text-[#D93829]" />
            </div>
            <div className="mt-3">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#68655E]">
                Editorial &amp; General
              </div>
              <div className="font-serif-display text-lg text-[#1C1C1A] mt-1">
                hello@maildirect.store
              </div>
            </div>
          </a>

          <a
            href="mailto:press@maildirect.store"
            className="group polaroid block rotate-[1deg] hover:rotate-0 transition-transform duration-500"
            data-testid="contact-press-card"
          >
            <div className="bg-[#EFECE5] h-40 flex items-center justify-center">
              <MessageCircle size={40} className="text-[#D93829]" />
            </div>
            <div className="mt-3">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#68655E]">
                Press &amp; Media
              </div>
              <div className="font-serif-display text-lg text-[#1C1C1A] mt-1">
                press@maildirect.store
              </div>
            </div>
          </a>

          <a
            href="mailto:partners@maildirect.store"
            className="group polaroid block rotate-[-3deg] hover:rotate-0 transition-transform duration-500"
            data-testid="contact-partners-card"
          >
            <div className="bg-[#EFECE5] h-40 flex items-center justify-center">
              <Send size={40} className="text-[#D93829]" />
            </div>
            <div className="mt-3">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#68655E]">
                Partnerships
              </div>
              <div className="font-serif-display text-lg text-[#1C1C1A] mt-1">
                partners@maildirect.store
              </div>
            </div>
          </a>
        </div>

        <div className="mt-16 border-t border-[#E4DFD4] pt-8 text-[13px] text-[#68655E] font-sans-body">
          <p>
            Postmark &amp; Co. is an independent editorial team based in the
            United States. We&rsquo;re not a printer or mailhouse ourselves —
            when a reader needs to actually send a postcard, we point them to
            our partner Thanks.io.
          </p>
        </div>
      </div>
    </PageShell>
  );
};

export default Contact;
